import { useEffect, useState } from "react";
import { useSubs } from "@/context/SubsContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { User, Bell, Trash2, ShieldCheck } from "lucide-react";

export default function SettingsPage() {
  const { settings, saveSettings, clearDemo, subs } = useSubs();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  if (!form) {
    return <div className="text-gray-500 py-10 text-center">Chargement…</div>;
  }

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSettings(form);
    } finally {
      setSaving(false);
    }
  };

  const demoCount = subs.filter((s) => s.is_demo).length;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-root">
      <section className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <User size={18} className="text-emerald-700" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-gray-900">Profil</h3>
            <p className="text-sm text-gray-500">Vos informations personnelles de base.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nom</Label>
            <Input
              id="name"
              data-testid="settings-name"
              value={form.name || ""}
              onChange={update("name")}
              placeholder="Votre nom"
              className="rounded-xl focus-visible:ring-emerald-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              data-testid="settings-email"
              value={form.email || ""}
              onChange={update("email")}
              placeholder="vous@exemple.com"
              className="rounded-xl focus-visible:ring-emerald-500"
            />
          </div>
        </div>
      </section>

      <section className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Bell size={18} className="text-emerald-700" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-gray-900">Préférences</h3>
            <p className="text-sm text-gray-500">Devise, notifications et rappels.</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Devise</Label>
              <Select value={form.currency} onValueChange={update("currency")}>
                <SelectTrigger data-testid="settings-currency" className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">Euro (€)</SelectItem>
                  <SelectItem value="USD">Dollar ($)</SelectItem>
                  <SelectItem value="GBP">Livre (£)</SelectItem>
                  <SelectItem value="CHF">Franc suisse (CHF)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">L'affichage utilise l'euro par défaut dans cette version.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notifydays">Rappel avant renouvellement</Label>
              <Input
                id="notifydays"
                type="number"
                min="0"
                max="30"
                data-testid="settings-notifydays"
                value={form.notify_days_before}
                onChange={(e) => setForm((f) => ({ ...f, notify_days_before: parseInt(e.target.value || "0", 10) }))}
                className="rounded-xl focus-visible:ring-emerald-500"
              />
              <p className="text-xs text-gray-500">Nombre de jours à l'avance.</p>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-gray-100 p-4">
            <div>
              <div className="font-medium text-gray-900">Activer les rappels</div>
              <div className="text-xs text-gray-500">L'envoi effectif nécessite une intégration e-mail/push (à configurer).</div>
            </div>
            <Switch
              checked={form.notifications_enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, notifications_enabled: v }))}
              data-testid="settings-notifications"
              className="data-[state=checked]:bg-emerald-600"
            />
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="settings-save"
            className="rounded-full bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </section>

      <section className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <ShieldCheck size={18} className="text-emerald-700" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-gray-900">Confidentialité</h3>
            <p className="text-sm text-gray-500">Vos données restent les vôtres.</p>
          </div>
        </div>
        <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
          <li>SubSave ne demande jamais vos mots de passe ni vos coordonnées bancaires.</li>
          <li>Les données sont saisies manuellement et stockées de façon sécurisée.</li>
          <li>Aucune résiliation automatique n'est effectuée sans votre action explicite.</li>
          <li>L'authentification sécurisée et la synchronisation chiffrée seront ajoutées dans une version future.</li>
        </ul>
      </section>

      {demoCount > 0 && (
        <section className="rounded-2xl bg-white p-6 border border-amber-100 shadow-soft">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <Trash2 size={18} className="text-amber-700" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-gray-900">Données de démonstration</h3>
              <p className="text-sm text-gray-500">
                {demoCount} abonnement{demoCount > 1 ? "s" : ""} de démo sont présents. Supprimez-les pour commencer à utiliser SubSave avec vos vraies données.
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setConfirmClear(true)}
              data-testid="clear-demo-btn"
              className="rounded-full text-amber-700 border-amber-200 hover:bg-amber-50"
            >
              Supprimer toutes les données de démo
            </Button>
          </div>
        </section>
      )}

      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer les données de démo ?</AlertDialogTitle>
            <AlertDialogDescription>
              Tous les abonnements marqués comme « Démo » seront définitivement supprimés. Vos abonnements personnels seront conservés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-clear-demo"
              className="rounded-full bg-red-600 hover:bg-red-700"
              onClick={async () => {
                setConfirmClear(false);
                await clearDemo();
              }}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
